import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, Loader2, PackageSearch, Globe2, Star, RotateCcw, Search, ChevronDown } from "lucide-react";
import api, { inr, fmtErr } from "../../lib/api";
import { toast } from "sonner";

const inputCls = "mono w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-shipany-cyan focus:outline-none";
const labelCls = "mb-1 block text-[11px] font-bold uppercase tracking-wider text-slate-400";


function SearchableCountrySelect({ countries, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = countries.find((c) => c.code === value);
  const filtered = countries.filter((c) => !q || c.name.toLowerCase().includes(q.toLowerCase()) || c.code.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="relative" data-testid="pricing-intl-country-select">
      <button type="button" onClick={() => setOpen(!open)} data-testid="pricing-intl-country-trigger"
        className="flex w-full items-center justify-between rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-left text-sm text-white focus:border-shipany-cyan focus:outline-none">
        {selected ? <span>{selected.name} <span className="mono text-xs text-slate-400">{selected.code}</span></span> : <span className="text-slate-500">Search {countries.length || "all"} countries…</span>}
        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="absolute z-30 mt-2 w-full overflow-hidden rounded-xl border border-white/15 bg-[#071B3A] shadow-2xl" data-testid="pricing-country-dropdown">
          <div className="flex items-center gap-2 border-b border-white/10 px-3 py-2">
            <Search className="h-4 w-4 text-slate-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="pricing-intl-country-search"
              placeholder="Type country name or code…" className="w-full bg-transparent py-1.5 text-sm text-white placeholder:text-slate-500 focus:outline-none" />
          </div>
          <div className="max-h-64 overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.code} type="button" data-testid={`pricing-country-option-${c.code}`}
                onClick={() => { onChange(c.code); setOpen(false); setQ(""); }}
                className="flex w-full items-center justify-between px-4 py-2.5 text-left text-sm text-slate-200 transition-colors hover:bg-white/10">
                <span>{c.name}</span><span className="mono text-xs text-slate-500">{c.code}</span>
              </button>
            ))}
            {!filtered.length && <p className="px-4 py-5 text-center text-xs text-slate-500" data-testid="pricing-country-empty">No matching country</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function PricingCalculator() {
  const [tab, setTab] = useState("domestic");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [countries, setCountries] = useState([]);

  const [dom, setDom] = useState({ pickup_pincode: "", delivery_pincode: "", weight: "1", length: "", breadth: "", height: "", package_type: "box", payment_mode: "Prepaid", cod_amount: "" });
  const [intl, setIntl] = useState({ destination_country: "", postal_code: "", state: "", weight: "1", length: "", breadth: "", height: "", declared_value: "2000" });

  useEffect(() => {
    api.get("/intl/countries").then(({ data }) => setCountries(data || [])).catch(() => {});
  }, []);

  const runDomestic = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(dom.pickup_pincode) || !/^\d{6}$/.test(dom.delivery_pincode)) {
      toast.error("Enter valid 6-digit pickup and delivery pincodes");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/rates/calculate", {
        pickup_pincode: dom.pickup_pincode,
        delivery_pincode: dom.delivery_pincode,
        weight: parseFloat(dom.weight) || 0.5,
        length: parseFloat(dom.length) || 10,
        breadth: parseFloat(dom.breadth) || 10,
        height: parseFloat(dom.height) || 10,
        package_type: dom.package_type,
        payment_mode: dom.payment_mode,
        cod_amount: dom.payment_mode === "COD" ? parseFloat(dom.cod_amount) || 0 : 0,
      });
      setResult({ kind: "domestic", data });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const runInternational = async (e) => {
    e.preventDefault();
    if (!intl.destination_country) {
      toast.error("Select a destination country");
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const { data } = await api.post("/rates/calculate/international", {
        destination_country: intl.destination_country,
        weight: parseFloat(intl.weight) || 0.5,
        length: parseFloat(intl.length) || 10,
        breadth: parseFloat(intl.breadth) || 10,
        height: parseFloat(intl.height) || 10,
        declared_value: parseFloat(intl.declared_value) || 1000,
        destination_postal_code: intl.postal_code,
        destination_state: intl.state,
      });
      setResult({ kind: "international", data });
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setLoading(false);
    }
  };

  const selectCls = "w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-white focus:border-shipany-cyan focus:outline-none";

  // Zone badges are meaningful only when one service has multiple zone-priced tiers
  // ShipAny Global Saver may show multiple destination-zone prices; carrier zone labels stay hidden.
  const zoneVariants = {};
  if (result?.kind === "international") {
    for (const q of result.data.quotes || []) {
      if (q.zone) (zoneVariants[q.courier_name] = zoneVariants[q.courier_name] || new Set()).add(q.zone);
    }
  }

  return (
    <div data-testid="pricing-calculator"
      className="relative overflow-hidden rounded-3xl border border-white/15 bg-white/[0.07] p-6 shadow-2xl shadow-black/40 backdrop-blur-2xl sm:p-8">
      <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-shipany-cyan/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-[#FF9933]/10 blur-3xl" />

      <div className="relative flex items-center gap-3">
        <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-[#FF9933] to-[#10B981]">
          <PackageSearch className="h-5 w-5 text-white" />
        </span>
        <div>
          <h3 className="font-display text-lg font-bold text-white">Full Price Calculator</h3>
          <p className="text-xs text-slate-400">Live final prices — same engine used at booking time.</p>
        </div>
      </div>

      <div className="relative mt-5 flex gap-2" data-testid="pricing-calc-tabs">
        {[["domestic", "Domestic", PackageSearch], ["international", "International", Globe2]].map(([key, label, Icon]) => (
          <button key={key} onClick={() => { setTab(key); setResult(null); }} data-testid={`pricing-calc-tab-${key}`}
            className={`flex flex-1 items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-all ${tab === key ? "bg-gradient-to-r from-[#FF9933] to-[#FF8000] text-[#071B3A]" : "border border-white/15 text-slate-300 hover:bg-white/5"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        {tab === "domestic" && !result && (
          <motion.form key="dom" onSubmit={runDomestic} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="relative mt-5 space-y-3" data-testid="pricing-calc-domestic-form">
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Pickup pincode</label>
                <input data-testid="dom-pickup-pincode" value={dom.pickup_pincode} maxLength={6} inputMode="numeric" onChange={(e) => setDom({ ...dom, pickup_pincode: e.target.value.replace(/\D/g, "") })} className={inputCls} placeholder="e.g. 500001" /></div>
              <div><label className={labelCls}>Delivery pincode</label>
                <input data-testid="dom-delivery-pincode" value={dom.delivery_pincode} maxLength={6} inputMode="numeric" onChange={(e) => setDom({ ...dom, delivery_pincode: e.target.value.replace(/\D/g, "") })} className={inputCls} placeholder="e.g. 110001" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><label className={labelCls}>Weight (kg)</label>
                <input data-testid="dom-weight" value={dom.weight} inputMode="decimal" onChange={(e) => setDom({ ...dom, weight: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>L (cm)</label>
                <input data-testid="dom-length" value={dom.length} inputMode="decimal" onChange={(e) => setDom({ ...dom, length: e.target.value })} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>B (cm)</label>
                <input data-testid="dom-breadth" value={dom.breadth} inputMode="decimal" onChange={(e) => setDom({ ...dom, breadth: e.target.value })} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>H (cm)</label>
                <input data-testid="dom-height" value={dom.height} inputMode="decimal" onChange={(e) => setDom({ ...dom, height: e.target.value })} className={inputCls} placeholder="10" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div><label className={labelCls}>Package type</label>
                <select data-testid="dom-package-type" value={dom.package_type} onChange={(e) => setDom({ ...dom, package_type: e.target.value })} className={selectCls}>
                  <option value="box" className="bg-[#071B3A]">Box / Parcel</option>
                  <option value="document" className="bg-[#071B3A]">Document (≤ 2 kg)</option>
                </select></div>
              <div><label className={labelCls}>Payment mode</label>
                <select data-testid="dom-payment-mode" value={dom.payment_mode} onChange={(e) => setDom({ ...dom, payment_mode: e.target.value })} className={selectCls}>
                  <option value="Prepaid" className="bg-[#071B3A]">Prepaid</option>
                  <option value="COD" className="bg-[#071B3A]">COD</option>
                </select></div>
              {dom.payment_mode === "COD" && (
                <div><label className={labelCls}>COD amount (₹)</label>
                  <input data-testid="dom-cod-amount" value={dom.cod_amount} inputMode="decimal" onChange={(e) => setDom({ ...dom, cod_amount: e.target.value })} className={inputCls} placeholder="e.g. 999" /></div>
              )}
            </div>
            <button data-testid="dom-calculate-btn" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.4)] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} CALCULATE DOMESTIC PRICE <ArrowRight className="h-4 w-4" />
            </button>
          </motion.form>
        )}

        {tab === "international" && !result && (
          <motion.form key="intl" onSubmit={runInternational} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} transition={{ duration: 0.25 }} className="relative mt-5 space-y-3" data-testid="pricing-calc-international-form">
            <div><label className={labelCls}>Destination country</label>
              <SearchableCountrySelect countries={countries} value={intl.destination_country} onChange={(v) => setIntl({ ...intl, destination_country: v })} /></div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div><label className={labelCls}>Destination state / region (zone pricing)</label>
                <input data-testid="intl-state" value={intl.state} onChange={(e) => setIntl({ ...intl, state: e.target.value })} className={inputCls} placeholder="e.g. New South Wales" /></div>
              <div><label className={labelCls}>Destination postcode (zone pricing)</label>
                <input data-testid="intl-postcode" value={intl.postal_code} onChange={(e) => setIntl({ ...intl, postal_code: e.target.value })} className={inputCls} placeholder="e.g. 2000" /></div>
            </div>
            <div className="grid gap-3 sm:grid-cols-4">
              <div><label className={labelCls}>Weight (kg)</label>
                <input data-testid="intl-weight" value={intl.weight} inputMode="decimal" onChange={(e) => setIntl({ ...intl, weight: e.target.value })} className={inputCls} /></div>
              <div><label className={labelCls}>L (cm)</label>
                <input data-testid="intl-length" value={intl.length} inputMode="decimal" onChange={(e) => setIntl({ ...intl, length: e.target.value })} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>W (cm)</label>
                <input data-testid="intl-breadth" value={intl.breadth} inputMode="decimal" onChange={(e) => setIntl({ ...intl, breadth: e.target.value })} className={inputCls} placeholder="10" /></div>
              <div><label className={labelCls}>H (cm)</label>
                <input data-testid="intl-height" value={intl.height} inputMode="decimal" onChange={(e) => setIntl({ ...intl, height: e.target.value })} className={inputCls} placeholder="10" /></div>
            </div>
            <div><label className={labelCls}>Declared shipment value (₹)</label>
              <input data-testid="intl-declared-value" value={intl.declared_value} inputMode="decimal" onChange={(e) => setIntl({ ...intl, declared_value: e.target.value })} className={inputCls} placeholder="e.g. 2000" /></div>
            <button data-testid="intl-calculate-btn" disabled={loading}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#FF9933] to-[#FF8000] py-3.5 font-display text-sm font-bold text-[#071B3A] transition-all hover:shadow-[0_8px_30px_rgba(255,153,51,0.4)] disabled:opacity-60">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} CALCULATE INTERNATIONAL PRICE <ArrowRight className="h-4 w-4" />
            </button>
            <p className="text-[11px] text-slate-500">Duties/customs at destination may apply as per local rules. Final billed weight is confirmed at the forwarding center.</p>
          </motion.form>
        )}

        {result?.kind === "domestic" && (
          <motion.div key="domres" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative mt-5" data-testid="domestic-results">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-display text-base font-bold text-white">{result.data.quotes?.length || 0} courier options</h4>
                <p className="mono text-xs text-shipany-cyan">{dom.pickup_pincode} → {dom.delivery_pincode} · chargeable {result.data.chargeable_weight} kg · {dom.payment_mode}</p>
              </div>
              <button onClick={() => setResult(null)} data-testid="dom-reset-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5"><RotateCcw className="h-3 w-3" /> New</button>
            </div>
            <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
              {(result.data.quotes || []).map((q, i) => (
                <div key={q.courier_id || i} data-testid={`dom-quote-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{q.courier_name}</p>
                    <p className="text-[11px] text-slate-400">{q.eta_days} day{q.eta_days > 1 ? "s" : ""} · COD {q.cod_supported !== false ? "available" : "—"}{q.badges?.[0] ? ` · ${q.badges[0]}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {q.best_price && <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[9px] font-bold uppercase text-[#10B981]">Best</span>}
                    <span className="mono text-base font-bold text-[#10B981]">{inr(q.final_price)}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {result?.kind === "international" && (
          <motion.div key="intlres" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="relative mt-5" data-testid="international-results">
            <div className="flex items-center justify-between">
              <div>
                <h4 className="font-display text-base font-bold text-white">{result.data.quotes?.length || 0} services to {result.data.country?.name}</h4>
                <p className="mono text-xs text-shipany-cyan">billed weight {result.data.totals?.billed} kg{result.data.zone_resolved ? ` · Zone ${result.data.zone_resolved} pricing applied` : ""} · duties may apply at destination</p>
              </div>
              <button onClick={() => setResult(null)} data-testid="intl-reset-btn" className="flex items-center gap-1.5 rounded-lg border border-white/15 px-3 py-1.5 text-xs font-semibold text-slate-300 hover:bg-white/5"><RotateCcw className="h-3 w-3" /> New</button>
            </div>
            <div className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
              {!result.data.quotes?.length && (
                <div className="rounded-xl border border-amber-400/25 bg-amber-400/10 px-4 py-4 text-sm text-amber-100" data-testid="intl-no-rates">
                  Live rates are currently unavailable for this destination. Please try another country or contact support.
                </div>
              )}
              {(result.data.quotes || []).map((q, i) => (
                <div key={i} data-testid={`intl-quote-${i}`} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-white">{q.courier_name}</p>
                    <p className="text-[11px] text-slate-400">{q.service}{q.zone && (zoneVariants[q.courier_name]?.size || 0) > 1 ? ` · Zone ${q.zone}` : ""}{q.transit_days ? ` · ${q.transit_days}` : ""}</p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    {q.recommended && <span className="rounded-full bg-[#10B981]/15 px-2 py-0.5 text-[9px] font-bold uppercase text-[#10B981]"><Star className="mr-0.5 inline h-2.5 w-2.5" />Best</span>}
                    <span className="mono text-base font-bold text-[#10B981]">{inr(q.customer_price)}</span>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
